'use client';

import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Loader2, Mail, UserPlus, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { ConnectionTable } from '@/components/admin/ConnectionTable';
import { AddAssignmentDialog } from './AddAssignmentDialog';
import { EditAssignmentDialog } from './EditAssignmentDialog';
import { authenticatedApiClient } from '@/lib/api/client';
import { initiativesApi } from '@/lib/api/endpoints';
import type { ApiConnection } from '@/lib/hooks/useConnections';

interface AssignmentTableBulkActionsProps {
  /** The initiative ID */
  initiativeId: number;
  /** Whether the current user can manage assignments */
  canManage?: boolean;
}

/**
 * AssignmentTableBulkActions
 *
 * Wraps ConnectionTable with selection + bulk action support for initiative assignments.
 * Bulk actions:
 * - Send Message: sends email to each selected user (prompts for subject + message)
 * - Remove Assignment: deletes each selected connection
 */
export function AssignmentTableBulkActions({
  initiativeId,
  canManage = false,
}: AssignmentTableBulkActionsProps) {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set());
  const [connections, setConnections] = useState<ApiConnection[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editConnection, setEditConnection] = useState<ApiConnection | null>(null);

  // Send Message dialog state
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');

  const handleDataChange = useCallback((data: ApiConnection[]) => {
    setConnections(data);
  }, []);

  // Bulk email mutation
  const bulkEmailMutation = useMutation({
    mutationFn: (data: { connectionIds: number[]; subject: string; message: string }) =>
      initiativesApi.sendBulkAssignmentEmail(initiativeId, data),
    onSuccess: (data) => {
      toast.success(`Email sent to ${data.sentCount} user${data.sentCount !== 1 ? 's' : ''}`);
      if (data.errors && data.errors.length > 0) {
        toast.error(`Some emails failed: ${data.errors.length} error(s)`);
      }
      setMessageDialogOpen(false);
      setMessageSubject('');
      setMessageContent('');
      setSelectedIds(new Set());
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to send emails';
      toast.error(message);
    },
  });

  // Bulk remove
  const [isRemoving, setIsRemoving] = useState(false);

  const handleBulkRemove = async () => {
    if (!confirm(`Remove ${selectedIds.size} assignment(s)?`)) return;

    setIsRemoving(true);
    const ids = Array.from(selectedIds).map(Number);
    let removedCount = 0;
    const errors: string[] = [];

    // Optimistic: remove from cache immediately
    queryClient.setQueriesData({ queryKey: ['connections'] }, (old: any) => {
      if (!old?.pages) return old;
      return {
        ...old,
        pages: old.pages.map((page: any) => ({
          ...page,
          connections: page.connections.filter((c: any) => !ids.includes(c.id)),
        })),
      };
    });

    for (const connId of ids) {
      try {
        await authenticatedApiClient.delete(`connections/${connId}`, { json: {} });
        removedCount++;
      } catch (err: any) {
        errors.push(`Connection ${connId}: ${err.message}`);
      }
    }

    setIsRemoving(false);

    if (removedCount > 0) {
      toast.success(`Removed ${removedCount} assignment${removedCount !== 1 ? 's' : ''}`);
      setSelectedIds(new Set());
    }
    if (errors.length > 0) {
      toast.error(`Failed to remove ${errors.length} assignment(s)`);
      // Rollback on errors
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    }
  };

  const handleOpenMessageDialog = () => {
    // Check that at least one selected connection is a user
    const selectedConnections = connections.filter((c) => selectedIds.has(c.id));
    const userConnections = selectedConnections.filter((c) => c.fromType === 'user');

    if (userConnections.length === 0) {
      toast.error('No users in selection to send messages to');
      return;
    }

    setMessageSubject('');
    setMessageContent('');
    setMessageDialogOpen(true);
  };

  const handleSendMessage = () => {
    if (!messageSubject.trim() || !messageContent.trim()) {
      toast.error('Subject and message are required');
      return;
    }

    const connectionIds = Array.from(selectedIds).map(Number);
    bulkEmailMutation.mutate({
      connectionIds,
      subject: messageSubject.trim(),
      message: messageContent.trim(),
    });
  };

  const handleDeleteConnection = async (connection: ApiConnection) => {
    if (!confirm('Remove this assignment?')) return;

    queryClient.setQueriesData({ queryKey: ['connections'] }, (old: any) => {
      if (!old?.pages) return old;
      return {
        ...old,
        pages: old.pages.map((page: any) => ({
          ...page,
          connections: page.connections.filter((c: any) => c.id !== connection.id),
        })),
      };
    });

    try {
      await authenticatedApiClient.delete(`connections/${connection.id}`, { json: {} });
      toast.success('Assignment removed');
    } catch (err: any) {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
      toast.error(err?.message || 'Failed to remove assignment');
    }
  };

  const selectedCount = selectedIds.size;
  const isSending = bulkEmailMutation.isPending;

  // Count users in selection for the message button label
  const selectedUserCount = connections.filter(
    (c) => selectedIds.has(c.id) && c.fromType === 'user'
  ).length;

  const [bulkAction, setBulkAction] = useState<string>('');

  const handleBulkActionSelect = (action: string) => {
    setBulkAction(action);
    switch (action) {
      case 'send-message':
        handleOpenMessageDialog();
        break;
      case 'remove':
        handleBulkRemove();
        break;
    }
    setBulkAction('');
  };

  const bulkActions = (
    <div className="flex items-center gap-2">
      <Select value={bulkAction} onValueChange={handleBulkActionSelect}>
        <SelectTrigger className="h-8 w-[160px] text-xs cursor-pointer">
          <SelectValue placeholder="Actions..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="send-message">
            Send Message{selectedUserCount > 0 ? ` (${selectedUserCount})` : ''}
          </SelectItem>
          <SelectItem value="remove">Remove ({selectedCount})</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <>
      <div className="space-y-4">
        {canManage && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAddOpen(true)}
              className="cursor-pointer"
            >
              <UserPlus className="w-4 h-4 mr-1.5" />
              Add Assignment
            </Button>
          </div>
        )}

        <ConnectionTable
          filters={{
            toType: 'initiative',
            toId: initiativeId,
            rolesOnly: true,
          }}
          hideToColumn
          enableSelection={canManage}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onDataChange={handleDataChange}
          bulkActions={selectedCount > 0 ? bulkActions : undefined}
          actions={
            canManage
              ? [
                  {
                    label: 'Edit Assignment',
                    icon: <Pencil className="h-4 w-4" />,
                    onClick: setEditConnection,
                  },
                  {
                    label: 'Remove Assignment',
                    icon: <Trash2 className="h-4 w-4" />,
                    onClick: handleDeleteConnection,
                    className: 'text-red-600',
                  },
                ]
              : []
          }
          columnOverrides={{
            from: { header: 'Assignee' },
            role: { hidden: true },
            tags: { hidden: true },
            created: { hidden: true },
            updated: { hidden: false },
            representing: { hidden: false },
          }}
          emptyState={{
            icon: <UserPlus className="w-10 h-10 text-gray-300" />,
            title: 'No assignments yet',
            description: canManage ? 'Add users or entities to this initiative' : undefined,
          }}
        />
      </div>

      <AddAssignmentDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        initiativeId={initiativeId}
        onSwitchToEdit={(connection) => {
          setAddOpen(false);
          setEditConnection(connection);
        }}
      />

      <EditAssignmentDialog
        open={!!editConnection}
        onOpenChange={(open) => {
          if (!open) setEditConnection(null);
        }}
        connection={editConnection}
      />

      {/* Send Message Dialog */}
      <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              Send Message to {selectedUserCount} user{selectedUserCount !== 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>Send an email to the selected assigned users.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="message-subject">Subject</Label>
              <Input
                id="message-subject"
                placeholder="Enter email subject..."
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message-content">Message</Label>
              <Textarea
                id="message-content"
                placeholder="Enter your message..."
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                rows={6}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMessageDialogOpen(false)}
              disabled={isSending}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendMessage}
              disabled={!messageSubject.trim() || !messageContent.trim() || isSending}
              className="cursor-pointer"
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
